import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaCheckDouble } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import PageHeader from '../../components/Shared/PageHeader';
import {
  createNewBranch,
  editBranch,
  getBranchList,
} from "../../store/slices/BusinessSlice";
import SharedButton from "../../components/Shared/SharedButton";
import TextInput from "../../components/Shared/TextInput";

export default function CreateBranchPage({
  setCreateDialogVisible,
}) {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { isLoading } = useSelector((state) => state.business);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location?.state?.editData || null;
  const initialValues = {
    name_en: editData?.name_en || "",
    name_ar: editData?.name_ar || "",
    address: editData?.address || "",
    contact_info: editData?.contact_info || "",
    business_id: editData?.business_id || user?.business_id,
  };

  const validationSchema = Yup.object().shape({
    name_en: Yup.string().required(t("required")),
    name_ar: Yup.string().required(t("required")),
    address: Yup.string()
      .required(t("required"))
      .matches(/^(?!\d+$).+$/, t("invalid_address_format")),
    contact_info: Yup.string().required(t("required")),
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      if (editData) {
        const resultAction = await dispatch(
          editBranch({ data: values, id: editData?.id })
        );
        if (editBranch.fulfilled.match(resultAction)) {
          if (setCreateDialogVisible && typeof setCreateDialogVisible === 'function') {
            setCreateDialogVisible(false);
            dispatch(getBranchList({ page: 1, per_page: 10 }));
          } else {
            navigate('/manager/branch');
          }
          resetForm();
        }
      } else {
        const resultAction = await dispatch(createNewBranch(values));
        if (createNewBranch.fulfilled.match(resultAction)) {
          if (setCreateDialogVisible && typeof setCreateDialogVisible === 'function') {
            setCreateDialogVisible(false);
            dispatch(getBranchList({ page: 1, per_page: 10 }));
          } else {
            navigate('/manager/branch');
          }
          resetForm();
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  return (
    <div className="flex flex-col w-full gap-2 max-h-[calc(100vh-150px)]">
      <PageHeader title={t(editData ? "Update Branch" : "Create Branch")} backPath={'/manager/branch'} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
              <TextInput
                label={t("name_en")}
                name="name_en"
                placeholder={`${t("enter")} ${t("name_en")}`}
              />

              <TextInput
                label={t("name_ar")}
                name="name_ar"
                placeholder={`${t("enter")} ${t("name_ar")}`}
              />

              <TextInput
                label={t("contact_info")}
                name="contact_info"
                placeholder={t("contact_info_placeholder")}
              />

              <TextInput
                label={t("address")}
                name="address"
                placeholder={t("address_placeholder")}
              />
            </div>

            <div className="flex justify-end gap-2">
              <SharedButton
                type="button"
                icon="pi pi-times"
                label={t("Cancel")}
                buttonType="reset"
                onClick={() => {
                  navigate("/manager/branch");
                  formik.resetForm();
                }}
              />
              <SharedButton
                type="submit"
                icon={<FaCheckDouble size={12} className='me-2' />}
                loading={isLoading}
                disabled={isLoading}
                label={editData ? t("Update") : t("Submit")}
                className="submit-btn"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}